import type { ReactNode } from "react";
import { useFormContext } from "@leagueplatform/web-common";
import { useIntl } from "@leagueplatform/locales";
import {
  FormField,
  Select,
  type GDSSelectProps,
} from "@leagueplatform/genesis-core";
import type { RHFInputBaseProps } from "./rhf-input.types";

export type SelectOption = {
  label: string;
  value: string;
};

export type RHFSelectProps = RHFInputBaseProps & {
  options: SelectOption[];
  placeholder?: string;
} & Omit<GDSSelectProps, "id" | "disabled" | "children">;

export const RHFSelect = ({
  id,
  label,
  hint,
  required,
  requiredMessage,
  registerOptions,
  options,
  placeholder,
  disabled,
  ...restComponentProps
}: RHFSelectProps) => {
  const {
    register,
    formState: { errors },
  } = useFormContext();
  const { formatMessage } = useIntl();

  const defaultRequiredMessage = formatMessage(
    { id: "STR_FIELD_REQUIRED" },
    { fieldName: label }
  );

  const registerProps = register(id, {
    ...registerOptions,
    required: required && (requiredMessage || defaultRequiredMessage),
  });

  const hasError = !!errors?.[id]?.message;

  return (
    <FormField
      id={id}
      label={label ?? ""}
      name={id}
      hideLabel={!label}
      hint={hint}
      required={required}
      inputStatus={hasError ? "error" : undefined}
      statusMessage={hasError ? (errors[id]?.message as ReactNode) : undefined}
      css={{
        ".GDS-input-status-message-wrapper": {
          ".GDS-icon": {
            display: "none",
          },
        },
      }}
    >
      <Select
        id={id}
        disabled={disabled}
        {...(hasError && { inputStatus: "error" })}
        {...restComponentProps}
        {...registerProps}
        placeholder={
          placeholder || formatMessage({ id: "STR_SELECT_PLACEHOLDER" })
        }
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
    </FormField>
  );
};
